import { useCallback, useEffect, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Button as MuiButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import { dayjs } from "@thalamos/common";
import type {
  ExtendedThalamosUser,
  InterviewContactMethod,
  InterviewParticipant,
  InterviewWithParticipants,
  UpdateInterviewInput,
} from "@thalamos/aspire-common";
import {
  notificationChannelFns,
  NotificationEvents,
} from "@thalamos/aspire-common";

import type { InterviewManagementDialogApi } from "./InterviewManagementDialog.js";
import { InterviewFormView } from "./InterviewManagementDialog.js";
import {
  getDisplayStatus,
  getStatusColor,
  getStatusIcon,
  getStatusLabel,
  getStatusTextColor,
} from "./participantStatusHelpers.js";
import { api } from "~/api.js";
import {
  Button,
  renderErrorToast as defaultRenderErrorToast,
  renderSuccessToast as defaultRenderSuccessToast,
  FormTitle,
} from "~/components/design-system/index.js";
import { Container, ExpandMore } from "~/components/layout/styleWrappers.js";
import { assertOkResponse } from "~/helpers/assertOkResponse.js";
import { useChannel } from "~/lib/pusher/useChannel.js";
import { useEvent } from "~/lib/pusher/useEvent.js";
import { ConfirmationModal } from "~/pages/ConfirmationModal.js";
import { routeFns } from "~/routePaths.js";

// ---------------------------------------------------------------------------
// API interface for dependency injection (testing / stories)
// ---------------------------------------------------------------------------

type ConfirmParticipantRequestBody = {
  noteToDoctor?: string | null;
};

/** API functions used by InterviewsSection — injectable for testing */
export interface InterviewsSectionApi {
  getForFormContext: (
    formContextId: string,
  ) => Promise<{ data: InterviewWithParticipants[] }>;
  confirmParticipant: (
    interviewId: string,
    participantId: string,
    body?: ConfirmParticipantRequestBody,
  ) => Promise<unknown>;
  revokeParticipant: (
    interviewId: string,
    participantId: string,
  ) => Promise<unknown>;
  unconfirmParticipant: (
    interviewId: string,
    participantId: string,
  ) => Promise<unknown>;
  restoreParticipants: (interviewId: string) => Promise<unknown>;
  update: (interviewId: string, body: UpdateInterviewInput) => Promise<unknown>;
}

/** Default API implementation wiring to the real API client */
const defaultApi: InterviewsSectionApi = {
  getForFormContext: (formContextId) =>
    api.interviews.getForFormContext(formContextId),
  confirmParticipant: (interviewId, participantId, body) =>
    api.interviews
      .confirmParticipant(interviewId, participantId, body)
      .then(assertOkResponse),
  revokeParticipant: (interviewId, participantId) =>
    api.interviews
      .revokeParticipant(interviewId, participantId)
      .then(assertOkResponse),
  unconfirmParticipant: (interviewId, participantId) =>
    api.interviews
      .unconfirmParticipant(interviewId, participantId)
      .then(assertOkResponse),
  restoreParticipants: (interviewId) =>
    api.interviews.restoreParticipants(interviewId).then(assertOkResponse),
  update: (interviewId, body) =>
    api.interviews.update(interviewId, body).then(assertOkResponse),
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

function getContactMethodLabel(
  contactMethod: InterviewContactMethod[] | null | undefined,
): string {
  if (!contactMethod || contactMethod.length === 0) return "Not recorded";
  const labelMap: Record<InterviewContactMethod, string> = {
    email: "Email",
    sms: "SMS",
    manual: "Logged manually",
    call: "Called",
  };
  const labels = contactMethod.map((m) => labelMap[m]);
  return new Intl.ListFormat("en", {
    style: "long",
    type: "conjunction",
  }).format(labels);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface InterviewsSectionProps {
  formContextId: string;
  patientId: string;
  user: ExtendedThalamosUser;
  reloadFormContext: () => void;
  /** Whether the AMHP has an active work item on this admission. When false,
   * interview management actions are disabled. */
  hasActiveWorkItem: boolean;
  /** Optional API override for testing / Storybook */
  apiOverride?: InterviewsSectionApi;
  /** Optional toast overrides for testing / Storybook */
  renderSuccessToast?: (props: { message: string }) => void;
  renderErrorToast?: (props: { message: string }) => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function InterviewsSection({
  formContextId,
  patientId,
  user: _user,
  reloadFormContext,
  hasActiveWorkItem,
  apiOverride,
  renderSuccessToast = defaultRenderSuccessToast,
  renderErrorToast = defaultRenderErrorToast,
}: InterviewsSectionProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const apiClient = apiOverride ?? defaultApi;
  const [expanded, setExpanded] = useState(true);
  const [interviews, setInterviews] = useState<
    InterviewWithParticipants[] | null
  >(null);
  const [loading, setLoading] = useState(true);

  // Inline action state
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  // Confirm-details dialog state
  const [confirmDetails, setConfirmDetails] = useState<{
    interview: InterviewWithParticipants;
    participantId: string;
  } | null>(null);

  const loadInterviews = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiClient.getForFormContext(formContextId);
      setInterviews(Array.isArray(response.data) ? response.data : []);
    } catch {
      setInterviews([]);
    } finally {
      setLoading(false);
    }
  }, [formContextId, apiClient]);

  useEffect(() => {
    void loadInterviews();
  }, [loadInterviews]);

  // Subscribe to pushed notifications so interviews auto-reload when changed
  const interviewsChannel = useChannel(
    notificationChannelFns.interviews(formContextId),
  );
  useEvent(interviewsChannel, NotificationEvents.resourceReload, () => {
    void loadInterviews();
  });

  const activeInterviews = (interviews ?? [])
    .filter((i) => i.status !== "cancelled")
    .sort((a, b) => a.created.localeCompare(b.created));

  const totalDoctors = new Set(
    activeInterviews.flatMap((i) => i.participants.map((p) => p.userId)),
  ).size;

  const summaryLabel =
    activeInterviews.length > 0
      ? ` (${String(totalDoctors)} ${totalDoctors === 1 ? "doctor" : "doctors"} over ${String(activeInterviews.length)} ${activeInterviews.length === 1 ? "assessment" : "assessments"})`
      : "";

  const handleConfirm = (interviewId: string, participantId: string) => {
    const interview = (interviews ?? []).find((i) => i.id === interviewId);
    if (interview) {
      setConfirmDetails({ interview, participantId });
    }
  };

  const handleRevoke = async (interviewId: string, participantId: string) => {
    setActionInProgress(participantId);
    try {
      await apiClient.revokeParticipant(interviewId, participantId);
      renderSuccessToast({ message: "Participant revoked" });
      await loadInterviews();
      reloadFormContext();
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleUnconfirm = async (
    interviewId: string,
    participantId: string,
  ) => {
    setActionInProgress(participantId);
    try {
      await apiClient.unconfirmParticipant(interviewId, participantId);
      renderSuccessToast({ message: "Doctor unconfirmed" });
      await loadInterviews();
      reloadFormContext();
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  const handleRestore = async (interviewId: string) => {
    setActionInProgress("restore-" + interviewId);
    try {
      await apiClient.restoreParticipants(interviewId);
      renderSuccessToast({ message: "Previous candidates restored" });
      await loadInterviews();
      reloadFormContext();
    } catch (error) {
      renderErrorToast({
        message:
          error instanceof Error
            ? error.message
            : "Something went wrong. Please try again.",
      });
    } finally {
      setActionInProgress(null);
    }
  };

  // Nothing to show while initial load is running
  if (loading && interviews === null) {
    return (
      <Container addMarginBottom>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            mb: 2,
          }}
        >
          <FormTitle
            subtitleText="Independent Doctors"
            hasContainerMarginBottom={false}
            subTitleTextFontSize={2}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            py: 3,
          }}
        >
          <CircularProgress size={24} />
        </Box>
      </Container>
    );
  }

  return (
    <Container addMarginBottom>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 2,
        }}
      >
        <FormTitle
          subtitleText={`Independent Doctors${summaryLabel}`}
          hasContainerMarginBottom={false}
          subTitleTextFontSize={2}
        />
        <Box sx={{ ml: "auto", display: "flex", gap: 1, alignItems: "center" }}>
          <Button
            variant="outlined"
            label="Manage"
            size="small"
            onClick={() => {
              navigate(
                routeFns.formContextInterviews(formContextId, patientId),
                { state: { initialView: "list" } },
              );
            }}
            testId="manage-interviews-button"
            disabled={!hasActiveWorkItem}
          />
          <ExpandMore
            role="button"
            isExpanded={expanded}
            onClick={() => {
              setExpanded(!expanded);
            }}
            aria-expanded={expanded}
            aria-label="Toggle independent doctors section"
          >
            <ExpandMoreIcon sx={{ color: "primary.main" }} />
          </ExpandMore>
        </Box>
      </Box>
      {expanded && (
        <>
          {activeInterviews.length === 0 && (
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                py: 2,
              }}
            >
              No independent doctors invited yet. Use &ldquo;Manage&rdquo; to
              create an assessment and invite doctors.
            </Typography>
          )}

          {activeInterviews.length > 0 &&
            (isMobile ? (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {activeInterviews.map((interview, index) => (
                  <InterviewTableGroup
                    key={interview.id}
                    interview={interview}
                    interviewLabel={`MHA Assessment ${String(index + 1)}`}
                    isMobile
                    actionInProgress={actionInProgress}
                    hasActiveWorkItem={hasActiveWorkItem}
                    onConfirm={(participantId) => {
                      handleConfirm(interview.id, participantId);
                    }}
                    onRevoke={(participantId) => {
                      void handleRevoke(interview.id, participantId);
                    }}
                    onUnconfirm={(participantId) => {
                      void handleUnconfirm(interview.id, participantId);
                    }}
                    onFindDoctor={() => {
                      navigate(
                        routeFns.formContextFindDoctors(
                          formContextId,
                          patientId,
                          interview.id,
                        ),
                      );
                    }}
                    onRestoreParticipants={() => {
                      void handleRestore(interview.id);
                    }}
                  />
                ))}
              </Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 600 }}>Doctor</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>
                        Contacted via
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Invited</TableCell>
                      <TableCell sx={{ fontWeight: 600 }} align="right">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeInterviews.map((interview, index) => (
                      <InterviewTableGroup
                        key={interview.id}
                        interview={interview}
                        interviewLabel={`MHA Assessment ${String(index + 1)}`}
                        actionInProgress={actionInProgress}
                        hasActiveWorkItem={hasActiveWorkItem}
                        onConfirm={(participantId) => {
                          handleConfirm(interview.id, participantId);
                        }}
                        onRevoke={(participantId) => {
                          void handleRevoke(interview.id, participantId);
                        }}
                        onUnconfirm={(participantId) => {
                          void handleUnconfirm(interview.id, participantId);
                        }}
                        onFindDoctor={() => {
                          navigate(
                            routeFns.formContextFindDoctors(
                              formContextId,
                              patientId,
                              interview.id,
                            ),
                          );
                        }}
                        onRestoreParticipants={() => {
                          void handleRestore(interview.id);
                        }}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ))}
        </>
      )}
      {/* Confirm-details intermediary dialog */}
      {confirmDetails && (
        <Dialog
          open
          onClose={() => {
            setConfirmDetails(null);
          }}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
          data-testid="confirm-details-dialog"
        >
          <DialogTitle>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6">Confirm Assessment Details</Typography>
              <IconButton
                onClick={() => {
                  setConfirmDetails(null);
                }}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent dividers>
            <InterviewFormView
              formContextId={formContextId}
              mode="confirm"
              initialValues={{
                address: confirmDetails.interview.address,
                postalCode: confirmDetails.interview.postalCode,
                locationUnknown: confirmDetails.interview.locationUnknown,
                timeRanges: confirmDetails.interview.timeRanges,
              }}
              onSave={async (updateBody, extra) => {
                await apiClient.update(confirmDetails.interview.id, updateBody);
                await apiClient.confirmParticipant(
                  confirmDetails.interview.id,
                  confirmDetails.participantId,
                  { noteToDoctor: extra?.noteToDoctor ?? null },
                );
                renderSuccessToast({
                  message: "Assessment details updated and doctor confirmed",
                });
                setConfirmDetails(null);
                await loadInterviews();
                reloadFormContext();
              }}
              onCancel={() => {
                setConfirmDetails(null);
              }}
              apiClient={
                {
                  // InterviewFormView needs this shape but only uses create in
                  // "create" mode — in "confirm" mode it delegates via onSave.
                  getForFormContext: apiClient.getForFormContext,
                  get: () =>
                    Promise.resolve({ data: confirmDetails.interview }),
                  create: () => Promise.resolve(),
                  update: apiClient.update,
                  cancel: () => Promise.resolve(),
                  addParticipant: () =>
                    Promise.resolve({ data: confirmDetails.interview }),
                  confirmParticipant: apiClient.confirmParticipant,
                  revokeParticipant: apiClient.revokeParticipant,
                  unconfirmParticipant: apiClient.unconfirmParticipant,
                  createWorkItem: () => Promise.resolve(),
                  ensureGuestUser: api.users.ensureGuestUser,
                } satisfies InterviewManagementDialogApi
              }
              renderSuccessToast={renderSuccessToast}
              renderErrorToast={renderErrorToast}
            />
          </DialogContent>
        </Dialog>
      )}
    </Container>
  );
}

// ---------------------------------------------------------------------------
// Interview Table Group – section heading + participant rows within the table
// ---------------------------------------------------------------------------

export function InterviewTableGroup({
  interview,
  interviewLabel,
  actionInProgress,
  onConfirm,
  onRevoke,
  onUnconfirm,
  onFindDoctor,
  onRestoreParticipants,
  isMobile = false,
  hasActiveWorkItem,
}: {
  interview: InterviewWithParticipants;
  interviewLabel: string;
  actionInProgress: string | null;
  onConfirm: (participantId: string) => void;
  onRevoke: (participantId: string) => void;
  onUnconfirm: (participantId: string) => void;
  onFindDoctor: () => void;
  onRestoreParticipants?: () => void;
  isMobile?: boolean;
  hasActiveWorkItem: boolean;
}) {
  const isEditable = interview.status === "created";

  // Show confirm button only when no participant is already confirmed
  // or pending-confirmed
  const hasSelectedDoctor = interview.participants.some(
    (p) => p.status === "confirmed" || p.status === "pending-confirmed",
  );

  // Check if a pending-confirmed doctor rejected (all others are revoked)
  // This triggers a banner offering to restore previous candidates
  const hasPendingConfirmedRejection =
    interview.status === "created" &&
    interview.participants.some((p) => p.status === "rejected") &&
    interview.participants.some((p) => p.status === "revoked") &&
    !interview.participants.some(
      (p) =>
        p.status === "confirmed" ||
        p.status === "pending-confirmed" ||
        p.status === "accepted" ||
        p.status === "pending",
    );

  if (isMobile) {
    return (
      <Box sx={{ mb: 1 }}>
        {/* Assessment header */}
        <Box
          data-testid="interview-section-header"
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            px: 1.5,
            py: 1,
            bgcolor: "grey.50",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
            mb: 1,
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {interviewLabel}
              </Typography>
              {interview.status === "cancelled" && (
                <Chip label="Cancelled" size="small" color="error" />
              )}
            </Box>
            {interview.address && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
              >
                {interview.address}
                {interview.postalCode && `, ${interview.postalCode}`}
              </Typography>
            )}
            {interview.locationUnknown && (
              <Typography
                variant="caption"
                sx={{ color: "text.secondary", display: "block", mt: 0.25 }}
              >
                Location TBC
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={onFindDoctor}
            disabled={hasSelectedDoctor || !hasActiveWorkItem}
            aria-label={
              hasSelectedDoctor
                ? "Revoke the confirmed doctor before inviting another"
                : "Find a doctor"
            }
            title={
              hasSelectedDoctor
                ? "Revoke the confirmed doctor before inviting another"
                : "Find a doctor"
            }
            data-testid="find-doctor-button"
            color="primary"
            sx={{ ml: 1, flexShrink: 0 }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </Box>
        {/* Participant cards */}
        {interview.participants.map((participant) => (
          <ParticipantRow
            key={participant.id}
            participant={participant}
            isEditable={isEditable}
            hasSelectedDoctor={hasSelectedDoctor}
            actionInProgress={actionInProgress}
            hasActiveWorkItem={hasActiveWorkItem}
            onConfirm={() => {
              onConfirm(participant.id);
            }}
            onRevoke={() => {
              onRevoke(participant.id);
            }}
            onUnconfirm={() => {
              onUnconfirm(participant.id);
            }}
            isMobile
          />
        ))}
        {/* Restore banner */}
        {hasPendingConfirmedRejection && (
          <Box
            sx={{
              p: 1.5,
              bgcolor: "warning.50",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "warning.200",
              mt: 1,
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
              The selected doctor has declined. You can restore previously
              contacted doctors or start a new search.
            </Typography>
            <MuiButton
              fullWidth
              size="small"
              variant="outlined"
              onClick={() => onRestoreParticipants?.()}
              disabled={!hasActiveWorkItem}
              data-testid="restore-participants-button"
            >
              Restore Previous Candidates
            </MuiButton>
          </Box>
        )}
        {/* Empty state */}
        {interview.participants.length === 0 && (
          <Typography variant="body2" sx={{ color: "text.secondary", px: 0.5 }}>
            No doctors contacted yet.
          </Typography>
        )}
      </Box>
    );
  }

  return (
    <>
      {/* Section heading row spanning all columns */}
      <TableRow data-testid="interview-section-header">
        <TableCell
          colSpan={4}
          sx={{
            bgcolor: "grey.50",
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
              }}
            >
              {interviewLabel}
            </Typography>
            {interview.status === "cancelled" && (
              <Chip label="Cancelled" size="small" color="error" />
            )}
            {interview.address && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                {interview.address}
                {interview.postalCode && `, ${interview.postalCode}`}
              </Typography>
            )}
            {interview.locationUnknown && (
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                }}
              >
                Location TBC
              </Typography>
            )}
          </Box>
        </TableCell>
        <TableCell
          align="right"
          sx={{
            bgcolor: "grey.50",
            py: 0.75,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <IconButton
            size="small"
            onClick={onFindDoctor}
            disabled={hasSelectedDoctor || !hasActiveWorkItem}
            title={
              hasSelectedDoctor
                ? "Revoke the confirmed doctor before inviting another"
                : "Find a doctor"
            }
            data-testid="find-doctor-button"
            color="primary"
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </TableCell>
      </TableRow>
      {/* Participant rows */}
      {interview.participants.map((participant) => (
        <ParticipantRow
          key={participant.id}
          participant={participant}
          isEditable={isEditable}
          hasSelectedDoctor={hasSelectedDoctor}
          actionInProgress={actionInProgress}
          hasActiveWorkItem={hasActiveWorkItem}
          onConfirm={() => {
            onConfirm(participant.id);
          }}
          onRevoke={() => {
            onRevoke(participant.id);
          }}
          onUnconfirm={() => {
            onUnconfirm(participant.id);
          }}
        />
      ))}
      {/* Banner when a pending-confirmed doctor rejected */}
      {hasPendingConfirmedRejection && (
        <TableRow>
          <TableCell colSpan={5} sx={{ py: 1.5, bgcolor: "warning.50" }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  flex: 1,
                }}
              >
                The selected doctor has declined. You can restore previously
                contacted doctors or start a new search.
              </Typography>
              <MuiButton
                size="small"
                variant="outlined"
                onClick={() => onRestoreParticipants?.()}
                disabled={!hasActiveWorkItem}
                data-testid="restore-participants-button"
              >
                Restore Previous Candidates
              </MuiButton>
            </Box>
          </TableCell>
        </TableRow>
      )}
      {interview.participants.length === 0 && (
        <TableRow>
          <TableCell colSpan={5}>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
              }}
            >
              No doctors contacted yet.
            </Typography>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Participant Row – table row with Doctor, Status, Contacted via, Invited, Actions
// ---------------------------------------------------------------------------

export function ParticipantRow({
  participant,
  isEditable,
  hasSelectedDoctor,
  actionInProgress,
  hasActiveWorkItem,
  onConfirm,
  onRevoke: _onRevoke,
  onUnconfirm,
  isMobile = false,
}: {
  participant: InterviewParticipant;
  isEditable: boolean;
  hasSelectedDoctor: boolean;
  actionInProgress: string | null;
  hasActiveWorkItem: boolean;
  onConfirm: () => void;
  onRevoke: () => void;
  onUnconfirm: () => void;
  isMobile?: boolean;
}) {
  const [showUnconfirmDialog, setShowUnconfirmDialog] = useState(false);
  const isLoading = actionInProgress === participant.id;

  const displayStatus = getDisplayStatus(participant);

  // Confirm is available for pending/accepted/rejected/revoked when no doctor
  // is already confirmed or pending-confirmed on this interview.
  // Expired pending invites cannot be confirmed.
  const canConfirm =
    isEditable &&
    hasActiveWorkItem &&
    !hasSelectedDoctor &&
    displayStatus !== "expired" &&
    (participant.status === "pending" ||
      participant.status === "accepted" ||
      participant.status === "rejected" ||
      participant.status === "revoked");

  // Unconfirm is available for confirmed or pending-confirmed participants.
  const canUnconfirm =
    isEditable &&
    hasActiveWorkItem &&
    (participant.status === "confirmed" ||
      participant.status === "pending-confirmed");

  const isConfirmed =
    participant.status === "confirmed" ||
    participant.status === "pending-confirmed";

  if (isMobile) {
    const chipLabel =
      participant.status === "rejected" && participant.rejectionReason
        ? `Not Available: ${participant.rejectionReason}`
        : getStatusLabel(displayStatus);

    return (
      <>
        <Card
          variant="outlined"
          data-testid="participant-row"
          sx={{
            mb: 1,
            opacity: hasSelectedDoctor && !isConfirmed ? 0.7 : 1,
          }}
        >
          <CardContent>
            {/* Doctor name + status chip */}
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 1,
                mb: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: isConfirmed ? "bold" : "normal",
                  flex: 1,
                  minWidth: 0,
                  wordBreak: "break-word",
                }}
              >
                {participant.userName || participant.userEmail || "Unknown"}
              </Typography>
              <Tooltip title={chipLabel} arrow>
                <Chip
                  icon={getStatusIcon(displayStatus)}
                  label={chipLabel}
                  size="small"
                  color={getStatusColor(displayStatus)}
                  variant="filled"
                  sx={{
                    flexShrink: 0,
                    height: 22,
                    "& .MuiChip-label": {
                      px: 0.75,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: getStatusTextColor(displayStatus),
                    },
                    "& .MuiChip-icon": {
                      color: `${getStatusTextColor(displayStatus)} !important`,
                    },
                  }}
                />
              </Tooltip>
            </Box>
            {/* 2-column metadata grid */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  Contacted via
                </Typography>
                <Typography variant="body2">
                  {getContactMethodLabel(participant.contactMethod)}
                </Typography>
              </Box>
              <Box>
                <Typography
                  variant="caption"
                  sx={{ color: "text.secondary", display: "block" }}
                >
                  Invited
                </Typography>
                <Typography variant="body2">
                  {dayjs(participant.created).format("DD MMM YYYY")}
                </Typography>
              </Box>
            </Box>
          </CardContent>
          {(canConfirm || canUnconfirm) && (
            <CardActions sx={{ pt: 0, px: 2, pb: 1.5 }}>
              {canConfirm && (
                <MuiButton
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={onConfirm}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={14} /> : "Confirm"}
                </MuiButton>
              )}
              {canUnconfirm && (
                <MuiButton
                  fullWidth
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    setShowUnconfirmDialog(true);
                  }}
                  disabled={isLoading}
                >
                  {isLoading ? <CircularProgress size={14} /> : "Unconfirm"}
                </MuiButton>
              )}
            </CardActions>
          )}
        </Card>
        {showUnconfirmDialog && (
          <ConfirmationModal
            message={`Are you sure you want to unconfirm ${
              participant.userName || participant.userEmail || "this doctor"
            }? They will be moved back to their previous status.`}
            confirmFn={() => {
              setShowUnconfirmDialog(false);
              onUnconfirm();
            }}
            closeFn={() => {
              setShowUnconfirmDialog(false);
            }}
          />
        )}
      </>
    );
  }

  return (
    <TableRow
      data-testid="participant-row"
      sx={{
        "& td": { py: 0.5 },
        opacity: hasSelectedDoctor && !isConfirmed ? 0.7 : 1,
      }}
    >
      {/* Doctor */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            fontWeight: isConfirmed ? "bold" : "normal",
          }}
        >
          {participant.userName || participant.userEmail || "Unknown"}
          {participant.teamType !== "independent" && participant.teamName && (
            <Typography
              component="span"
              variant="body2"
              sx={{ color: "text.secondary" }}
            >
              {" "}
              ({participant.teamName})
            </Typography>
          )}
        </Typography>
      </TableCell>
      {/* Status */}
      <TableCell>
        {(() => {
          const chipLabel =
            participant.status === "rejected" && participant.rejectionReason
              ? `Not Available: ${participant.rejectionReason}`
              : getStatusLabel(displayStatus);

          return (
            <Tooltip title={chipLabel} arrow>
              <Chip
                icon={getStatusIcon(displayStatus)}
                label={chipLabel}
                size="small"
                color={getStatusColor(displayStatus)}
                variant="filled"
                sx={{
                  maxWidth: 180,
                  height: 22,
                  "& .MuiChip-label": {
                    px: 0.75,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: getStatusTextColor(displayStatus),
                  },
                  "& .MuiChip-icon": {
                    color: `${getStatusTextColor(displayStatus)} !important`,
                  },
                }}
              />
            </Tooltip>
          );
        })()}
      </TableCell>
      {/* Contacted via */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {getContactMethodLabel(participant.contactMethod)}
        </Typography>
      </TableCell>
      {/* Invited */}
      <TableCell>
        <Typography
          variant="body2"
          sx={{
            color: "text.secondary",
          }}
        >
          {dayjs(participant.created).format("DD MMM YYYY")}
        </Typography>
      </TableCell>
      {/* Actions */}
      <TableCell align="right">
        <Box
          sx={{
            display: "flex",
            gap: 0.5,
            justifyContent: "flex-end",
          }}
        >
          {canConfirm && (
            <MuiButton
              size="small"
              variant="outlined"
              color="primary"
              onClick={onConfirm}
              disabled={isLoading}
              sx={{
                py: 0.5,
                px: 1.5,
                height: "auto",
                minHeight: 0,
                fontSize: "0.75rem",
              }}
            >
              {isLoading ? <CircularProgress size={14} /> : "Confirm"}
            </MuiButton>
          )}
          {canUnconfirm && (
            <MuiButton
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => {
                setShowUnconfirmDialog(true);
              }}
              disabled={isLoading}
              sx={{
                py: 0.5,
                px: 1.5,
                height: "auto",
                minHeight: 0,
                fontSize: "0.75rem",
              }}
            >
              {isLoading ? <CircularProgress size={14} /> : "Unconfirm"}
            </MuiButton>
          )}
        </Box>
      </TableCell>
      {showUnconfirmDialog && (
        <ConfirmationModal
          message={`Are you sure you want to unconfirm ${participant.userName || participant.userEmail || "this doctor"}? They will be moved back to their previous status.`}
          confirmFn={() => {
            setShowUnconfirmDialog(false);
            onUnconfirm();
          }}
          closeFn={() => {
            setShowUnconfirmDialog(false);
          }}
        />
      )}
    </TableRow>
  );
}
